import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/auth-context";
import { BusinessAddNewProps } from "./business-add-new.props";
import { NewBusinessViewModel } from "./business-add-new.view-model";
import BusinessDetails from "../business-details/business-details";
import BusinessInfo from "../business-info/business-info";
import NewBusinessNumbers from "../business-numbers/business-numbers";
import ErrorPopup from "../error-popup/error-popup";
import ConfirmationPopup from "../confirmation-popup/confirmation-popup";

import {
	IBusinessDetailsModel,
	IBusinessInfoModel,
	INewBusinessModel,
	IContactModel,
	INewNumberModel,
} from "src/types/models/apiModels";
import ActionPanel from "../action-panel/action-panel";
import BusinessContact from "../business-contact/business-contact";
import DropdownCountry from "src/components/molecules/dropdown-country/dropdown-country";
import LoadingModal from "src/components/molecules/loading/LoadingModal";

const BusinessAddNew: React.FC<BusinessAddNewProps> = ({
	onSubmitClick,
	onCancelClick,
	editBusinessModel,
}) => {
	const { user } = useAuth(); // Extract user from useAuth hook
	const [loading, setLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [business, setBusiness] = useState<INewBusinessModel | undefined>(
		editBusinessModel
	);
	const [selectedCountryCode, setSelectedCountryCode] = useState("AU");
	const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
	const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
	const [error, setError] = useState<string>("");
	const viewModel = useRef<NewBusinessViewModel | null>(null);

	useEffect(() => {
		const fetchData = async () => {
			setLoading((prevValue) => {
				if (!prevValue) {
					return true;
				}
				return prevValue;
			});
			try {
				if (user && user.accessToken) {
					viewModel.current = new NewBusinessViewModel(
						user.accessToken
					);
					const response =
						await viewModel.current?.initBusiness(business);
					setBusiness(() => response);
				}
			} catch (error) {
				console.error("Error fetching initBusiness:", error);
			} finally {
				setLoading((prevValue) => {
					if (prevValue) {
						return false;
					}
					return prevValue;
				});
			}
		};

		fetchData();
	}, [user]);

	const handleInfoChange = (info: IBusinessInfoModel) => {
		if (business) {
			business.info = info;
			setBusiness(() => business);
		}
	};
	const handleContactChange = (contact: IContactModel) => {
		if (business) {
			business.primaryContact = contact;
			setBusiness(() => business);
		}
	};
	const handleDetailsChange = (details: IBusinessDetailsModel) => {
		if (business) {
			business.details = details;
			setBusiness(() => business);
		}
	};
	const handleNumbersChange = (numbers: INewNumberModel[]) => {
		if (business) {
			business.numbers = numbers;
			setBusiness(() => business);
		}
	};
	const handleSubmitBusiness = async () => {
		setBusiness(() => business);
		if (business) {
			const validate =
				await viewModel.current?.validateBusiness(business);
			if (validate && validate.length > 0) {
				setError(() => validate.join(","));
				toggleError(true);
			} else {
				setConfirmDialogOpen((prevValue) => {
					if (prevValue === false) {
						return true;
					}
					return prevValue;
				});
			}
		}
	};
	const handleConfirmSubmitBusiness = async () => {
		if (business) {
			setIsSaving(true);
			const response = await viewModel.current?.saveBusiness(business);
			if (response?.result === true) {
				onSubmitClick(business);
			} else {
				setError(() => response?.error ?? "");
				toggleError(true);
			}
			setIsSaving(false);
		}
	};
	const toggleError = (value: boolean) => {
		console.log("In toggleError");
		setErrorDialogOpen((prevValue) => {
			if (prevValue !== value) {
				return value;
			}
			return prevValue;
		});
	};
	const closeError = () => {
		console.log("In closeError");
		toggleError(false);
	};
	const closeConfirm = () => {
		setConfirmDialogOpen((prevValue) => {
			if (prevValue === true) {
				return false;
			}
			return prevValue;
		});
	};
	const handleCancel = () => {
		console.log("In handleCancel");

		onCancelClick();
	};

	if (loading) {
		return (
			<LoadingModal
				message="We're setting things up for you. Just a moment!"
				open={loading}
			/>
		);
	}
	return (
		<div className="bg-white flex flex-col items-center justify-center gap-6 p-12 rounded-lg drop-shadow-2xl">
			<BusinessContact
				editContact={business?.primaryContact}
				onChange={handleContactChange}
				useLoggedInUser={true}
			></BusinessContact>
			<BusinessInfo
				info={business?.info}
				onChange={handleInfoChange}
			></BusinessInfo>
			<div className="flex flex-col items-start justify-start gap-2 w-full">
				<span className="font-Poppins font-semibold text-md text-sky-800">
					Country of Operations
				</span>
				<DropdownCountry
					onSelect={(selectedCountry: string): void => {
						if (selectedCountryCode !== selectedCountry) {
							setBusiness((prevBusiness) => {
								if (!prevBusiness) return prevBusiness;

								// Create a copy of the business object
								const updatedBusiness = { ...prevBusiness };

								// Reset details if country code changes
								updatedBusiness.details = undefined;

								// Update country code
								if (updatedBusiness.info) {
									updatedBusiness.info = {
										...updatedBusiness.info,
										countryCode: selectedCountry,
									};
								}

								return updatedBusiness;
							});
						}

						setSelectedCountryCode(selectedCountry);
					}}
					selectedShortCode={selectedCountryCode}
				/>
			</div>
			<BusinessDetails
				businessDetails={business?.details}
				onChange={handleDetailsChange}
				businessCountryCode={selectedCountryCode}
			></BusinessDetails>
			<NewBusinessNumbers
				editNumbers={business?.numbers}
				onChange={handleNumbersChange}
			></NewBusinessNumbers>
			<ErrorPopup
				message={error}
				open={errorDialogOpen}
				onClose={closeError}
			></ErrorPopup>
			<ConfirmationPopup
				business={business}
				onConfirm={handleConfirmSubmitBusiness}
				open={confirmDialogOpen}
				onClose={closeConfirm}
			></ConfirmationPopup>
			<ActionPanel
				primaryButtonLabel={"Review"}
				primaryButtonEnabled={true}
				secondaryButtonLabel={"Cancel"}
				secondaryButtonEnabled={true}
				primaryButtonAction={handleSubmitBusiness}
				secondaryButtonAction={handleCancel}
			></ActionPanel>
			{isSaving && business && (
				<LoadingModal
					open={isSaving}
					message={`Registering ${business.info?.name}`}
				></LoadingModal>
			)}
		</div>
	);
};

export default BusinessAddNew;
